<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="case"
    :class="{ 'is-visible': isVisible, 'has-scrolled': hasScrolled }"
  >
    <div class="wrapper">
      <div class="case-header">
        <ArticleHeader ref="headerRef" :content="content.details" class="m-d" />
        <h1
          ref="titleRef"
          class="title split-title m-d"
          v-html="useLineBreak(content.title)"
        ></h1>
        <div
          ref="placeholderRef"
          class="placeholder"
          :style="`height: ${placeholderSizes.height}px; margin-top: ${placeholderSizes.titleOverlap}px;`"
        ></div>
      </div>
      <IntroWithCta
        v-if="content.excerpt"
        :content="{
          label: content.excerpt.headline,
          htmltext: content.excerpt.htmltext,
          cta: {
            label:
              chrome.language === 'en' ? 'Launch Project' : 'Voir le projet',
            url: content.link?.url,
          },
          isFiftyFifty: true,
        }"
        :class="{ 'is-visible': hasOnboarded }"
        class="case-intro m-d"
      />
    </div>

    <div class="case-content" :class="{ 'is-visible': hasOnboarded }">
      <div
        v-for="(cpt, i) in content.flexibleContent"
        :key="i"
        :class="cptClass(cpt.component as string) || defaultClasses"
      >
        <component
          :is="component[cpt.component as keyof typeof flexibleComponentList]"
          v-if="
            component[cpt.component as keyof typeof flexibleComponentList] &&
            cpt.content
          "
          :content="cpt.content"
        />
      </div>

      <div v-if="content.backLink" class="wrapper">
        <div class="case__back">
          <router-link :to="content.backLink.url" class="case__back__link pb-d">
            <SvgSprite
              symbol="ui-full-arrow"
              size="24"
              class="case__back__icon"
            />

            {{ content.backLink.label }}
          </router-link>
        </div>

        <Cta v-if="content.nextCase" :content="content.nextCase" class="m-d" />
      </div>
    </div>

    <GCtaRounded
      :text="t('global-top')"
      size="small"
      icon="arrowTop"
      :repeat="2"
      name="read-case"
      :foreground="resource.content.colors.foreground"
      :background="resource.content.colors.background"
      class="case__back-to-top"
      @click="onScrollToTop"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  reactive,
  onMounted,
  PropType,
  onBeforeUnmount,
  nextTick,
} from 'vue'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

import ArticleHeader from '@/components/ui/ArticleHeader.vue'
import Cta from '@/components/ui/Cta.vue'
import IntroWithCta from '@/components/ui/IntroWithCta.vue'

import { useLineBreak } from '@/utils/filters'
import { createSplitTitle, SplitTitle } from '@/animations/splitTitle'
import { useUiStore } from '@/stores/ui'
import { useNavigationStore } from '@/stores/navigation'
import { useResourceStore } from '@/stores/resource'
import { ComponentType } from '@/types'
import { CaseSingle, CaseHighlight } from '@/types/views/cases'
import { useResize, useScroll } from '@/utils/aware'
import { getOffset } from '@/utils/dom'
import { useChromeStore } from '@/stores/chrome'

import { poster } from '@/components/global/Poster.vue'
import flexibleComponentList from '@/modules/flexibleComponentList'

gsap.registerPlugin(ScrollToPlugin)

const props = defineProps({
  content: {
    type: Object as PropType<CaseSingle>,
    required: true,
  },
})

const ui = useUiStore()
const navigation = useNavigationStore()
const resource = useResourceStore()
const isFound = resource.template !== 'notfound'

const hasScrolled = ref(false)
const scroll = useScroll({
  callback: () => {
    hasScrolled.value = scroll.y.value > 1000
  },
})

const { t, ...chrome } = useChromeStore()

const onScrollToTop = () => {
  gsap.to(window, {
    duration: 1,
    scrollTo: { y: 0, autoKill: false },
    ease: 'power4.out',
  })
}

/* eslint-disable indent */
const highlight: CaseHighlight = isFound
  ? {
      colors: props.content.colors,
      visual: props.content.visual,
      title: props.content.title,
      slug: props.content.slug,
      url: props.content.link.url,
      services: [],
    }
  : ({} as CaseHighlight)
/* eslint-enable indent */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const component = flexibleComponentList()
const isVisible = ref(false)
const hasOnboarded = ref(false)

const titleRef = ref<HTMLElement>()

const placeholderRef = ref()
const placeholderSizes = reactive({
  titleOverlap: ui.isMediumLarger ? -160 : -40,
  height: 0,
})
const headerRef = ref<ComponentType<typeof ArticleHeader>>()
let splitTitle: SplitTitle

const cptClass = (name: string) => {
  const classes: Record<string, string[]> = {
    text: ['wrapper', 'm-intro-text'],
    gallery: ['case__gallery'],
    picture: ['wrapper', 'm-d', 'case__picture'],
    cta: ['wrapper', 'm-d', 'case__picture'],
  }

  return classes[name]
}
const defaultClasses = ['m-d', 'wrapper']
// DEV
// let posterOffset = 0

ui.$subscribe((_mutation, state) => {
  placeholderSizes.titleOverlap = state.isMediumLarger ? -160 : -40
})

const setPlaceholderHeight = () => {
  nextTick(() => {
    if (placeholderRef.value && poster.canvasRef.value) {
      placeholderSizes.height =
        poster.canvasRef.value.getBoundingClientRect().height
    }
  })
}

const posterOffset = computed(() => {
  if (titleRef.value) {
    const { height: titleHeight } = titleRef.value.getBoundingClientRect()
    const { top: titleTop } = getOffset(titleRef.value)

    return titleTop + titleHeight + placeholderSizes.titleOverlap
  }

  return 0
})

useResize(() => {
  setPlaceholderHeight()
})

onMounted(async () => {
  isVisible.value = true

  if (!titleRef.value || !headerRef.value) {
    return
  }

  ui.toggleScroll(true)
  splitTitle = createSplitTitle(titleRef.value)

  // If the current case is different than the previous one,
  // or if coming from portfolio + small screens (default transition)
  // init and wait for animate.
  if (
    poster.current?.slug !== highlight.slug ||
    (navigation.from === 'portfolio' && !ui.isMediumLarger) ||
    navigation.popstate
  ) {
    if (!ui.isMediumLarger) {
      // Mobile transition : top origin
      poster.position?.(posterOffset.value - 120 || 160)
    }

    await poster.init?.(highlight)
  }

  setPlaceholderHeight()

  poster.move?.({ template: 'case', offset: posterOffset })
  headerRef.value.play()
  splitTitle.intro.play()
  hasOnboarded.value = true
})

onBeforeUnmount(async () => {
  await splitTitle.outro.play().then()
})
</script>

<style lang="scss" scoped>
.case {
  visibility: hidden;

  &.is-visible {
    visibility: visible;
  }
}

.title {
  margin-bottom: 0;
}

.case-intro,
.case-content {
  opacity: 0;

  &.is-visible {
    opacity: 1;
  }
}

.case__gallery {
  // prettier-ignore
  @include fluid(margin-top,(xxs: 80px, xxl: 150px));
  @include fluid(
    margin-bottom,
    (
      xxs: 80px,
      xxl: 150px,
    )
  );
}

.case__picture {
  display: flex;
  overflow: hidden;

  :deep(.picture:not(.no-stretch)) {
    width: 100%;
  }

  :deep(.picture__img img) {
    height: auto;
    transform: translateY(10%) scale(1.1);
    transition: transform 2s $ease-out-quart;

    .is-visible & {
      transform: translateY(0) scale(1);
    }
  }
}

.case__back {
  @extend %fw-black;

  border: solid var(--c-foreground);
  border-width: 0 0 1px;
}

.case__back__link {
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--c-foreground);
  font-family: $ff-alt;
  font-size: 2rem;
  line-height: 2rem;
  transition: filter 0.25s;

  &:hover {
    // color: (var(--c-foreground-rgb), 0.5);
    filter: brightness(1.5);
  }
}

.case__back__icon {
  margin-right: 1.5rem;
  fill: var(--c-foreground);
  transform: rotate(180deg);
  transition:
    transform 0.25s,
    opacity 0.25s;

  .case__back:hover & {
    transform: translateX(-1rem) rotate(180deg);
  }
}

.case__back-to-top {
  position: fixed;
  z-index: 100;
  bottom: 3rem;
  left: 2rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s;

  .has-scrolled & {
    opacity: 1;
    pointer-events: auto;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  :deep(.text) {
    font-size: 0.6rem;
  }
}
</style>
